import React from 'react';
import { motion, type Variants } from 'framer-motion';
import {
    Trash2,
    UserRoundX,
    Mail,
    Smartphone,
    Database,
    ShieldCheck,
    Clock3,
    CreditCard,
    CheckCircle2,
    FileX,
    AlertTriangle,
    Sparkles,
    ChevronRight,
} from 'lucide-react';

const fadeUp: Variants = {
    hidden: { opacity: 0, y: 24 },
    show: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.45,
            ease: 'easeOut',
        },
    },
};

const QUICK_FACTS = [
    {
        icon: UserRoundX,
        title: 'Delete your account in the app',
        text: 'Users can remove their account and associated app data from within LesslyKilo settings.',
    },
    {
        icon: Mail,
        title: 'Request deletion by email',
        text: 'If you cannot access the app, you can contact us directly to request deletion.',
    },
    {
        icon: CreditCard,
        title: 'Subscriptions are managed separately',
        text: 'Deleting your account does not automatically cancel app store subscriptions.',
    },
];

const SECTIONS = [
    {
        id: 'overview',
        title: '1. Overview',
        icon: FileX,
        items: [
            {
                label: 'Your control over your data',
                text: 'LesslyKilo gives users a way to request deletion of their account and associated personal data. This page explains the available deletion methods, what data is affected, and what to expect after a request is submitted.',
            },
        ],
    },
    {
        id: 'delete-in-app',
        title: '2. Delete Your Account in the App',
        icon: Smartphone,
        items: [
            {
                label: 'Recommended method',
                text: 'If you can still access your LesslyKilo account, the fastest method is to use the account deletion option inside the app.',
            },
            {
                label: 'Suggested in-app path',
                text: 'Open LesslyKilo → Profile or Settings → Account → Delete Account.',
            },
            {
                label: 'Confirmation',
                text: 'You may be asked to confirm your identity or confirm the deletion action before your request is processed.',
            },
        ],
    },
    {
        id: 'request-by-email',
        title: '3. Request Deletion by Email',
        icon: Mail,
        items: [
            {
                label: 'If you cannot access the app',
                text: 'If you are unable to sign in, have lost access to your device, or need help, you may request account and data deletion by contacting us directly.',
            },
            {
                label: 'Send your request to',
                text: 'caselabs91@gmail.com',
            },
            {
                label: 'Include these details',
                text: 'Please include the email address linked to your LesslyKilo account and a clear statement that you want your account and associated data deleted.',
            },
            {
                label: 'Identity verification',
                text: 'For security, we may ask for reasonable information to verify that you are the rightful account holder before processing the deletion request.',
            },
        ],
    },
    {
        id: 'what-gets-deleted',
        title: '4. What Data We Delete',
        icon: Database,
        items: [
            {
                label: 'Account data',
                text: 'This may include your account profile information, linked email address, authentication-related records, and app profile details associated with your user account.',
            },
            {
                label: 'Health and tracking data',
                text: 'This may include weight logs, meal logs, hydration logs, workout logs, AI coaching history, progress records, and related app-generated insights associated with your account.',
            },
            {
                label: 'App data under our control',
                text: 'We will remove data from active systems that is associated with your LesslyKilo account, subject to legal, security, fraud-prevention, or operational retention requirements where applicable.',
            },
        ],
    },
    {
        id: 'what-may-be-retained',
        title: '5. What May Be Retained',
        icon: ShieldCheck,
        items: [
            {
                label: 'Legal or regulatory retention',
                text: 'Some limited information may be retained where required by law, regulation, dispute handling, fraud prevention, or security obligations.',
            },
            {
                label: 'Backups and delayed removal',
                text: 'Certain records may remain temporarily in secure backups before they are overwritten or removed according to our backup retention schedule.',
            },
            {
                label: 'Billing platform records',
                text: 'If you purchased a subscription through Google Play or Apple, some transaction records may remain with the relevant payment platform according to their own legal and operational obligations.',
            },
        ],
    },
    {
        id: 'timelines',
        title: '6. Deletion Timelines',
        icon: Clock3,
        items: [
            {
                label: 'Processing time',
                text: 'We aim to process valid deletion requests within a reasonable time after verification of the request.',
            },
            {
                label: 'Possible delays',
                text: 'Complex requests, identity verification issues, legal obligations, or platform-related constraints may extend processing time.',
            },
        ],
    },
    {
        id: 'subscriptions',
        title: '7. Subscriptions and Billing',
        icon: CreditCard,
        items: [
            {
                label: 'Important note',
                text: 'Deleting your LesslyKilo account does not automatically cancel a subscription purchased through Google Play Store or Apple App Store.',
            },
            {
                label: 'How to cancel',
                text: 'To stop future subscription charges, you must cancel the subscription directly in your app store account settings.',
            },
        ],
    },
    {
        id: 'contact',
        title: '8. Contact Us',
        icon: Mail,
        items: [
            {
                label: 'Email',
                text: 'caselabs91@gmail.com',
            },
            {
                label: 'Company',
                text: 'Viomath Tech Proprietary Limited',
            },
        ],
    },
] as const;

export default function DeletionPage() {
    return (
        <section className="relative overflow-hidden py-24 md:py-32">
            {/* Background */}
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(34,211,238,0.10),transparent_32%)]" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(16,185,129,0.08),transparent_28%)]" />
                <div className="absolute left-[-8%] top-20 h-72 w-72 rounded-full bg-cyan-400/10 blur-3xl" />
                <div className="absolute right-[-10%] bottom-10 h-80 w-80 rounded-full bg-emerald-400/10 blur-3xl" />
                <div className="absolute inset-0 opacity-[0.05] [background-image:linear-gradient(to_right,white_1px,transparent_1px),linear-gradient(to_bottom,white_1px,transparent_1px)] [background-size:48px_48px]" />
            </div>

            <div className="relative mx-auto max-w-7xl px-6">
                {/* Hero */}
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    animate="show"
                    className="mx-auto mb-16 max-w-4xl text-center"
                >
                    <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[var(--color-brand-primary)]/20 bg-[linear-gradient(135deg,rgba(34,211,238,0.10),rgba(16,185,129,0.08))] px-4 py-2 text-xs font-extrabold uppercase tracking-[0.22em] text-[var(--color-brand-primary)] shadow-[0_8px_30px_rgba(34,211,238,0.08)]">
                        <Trash2 size={14} />
                        Account & Data Deletion
                    </div>

                    <h1 className="text-4xl font-black leading-[1.05] text-[var(--color-brand-text)] md:text-6xl">
                        Need to delete your
                        <span className="block bg-[linear-gradient(90deg,var(--color-brand-primary),#34d399)] bg-clip-text text-transparent">
              LesslyKilo account or data?
            </span>
                    </h1>

                    <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-[var(--color-brand-text-secondary)] md:text-xl">
                        This page explains how to delete your account, how to request deletion of your data,
                        what gets removed, and what to expect during the process.
                    </p>

                    <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                        <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-[linear-gradient(135deg,rgba(34,211,238,0.08),rgba(255,255,255,0.02))] px-4 py-2 text-sm font-semibold text-[var(--color-brand-text)]">
                            <CheckCircle2 size={16} className="text-emerald-300" />
                            Delete in-app or by email
                        </div>
                        <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-[linear-gradient(135deg,rgba(34,211,238,0.08),rgba(255,255,255,0.02))] px-4 py-2 text-sm font-semibold text-[var(--color-brand-text)]">
                            <CheckCircle2 size={16} className="text-cyan-300" />
                            Clear deletion guidance
                        </div>
                    </div>
                </motion.div>

                {/* Quick facts */}
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: '-80px' }}
                    className="mb-10 rounded-[2.5rem] border border-[var(--color-brand-primary)]/12 bg-[linear-gradient(180deg,rgba(8,18,28,0.98),rgba(5,14,22,0.98))] p-8 shadow-[0_20px_60px_rgba(0,0,0,0.18)] md:p-10"
                >
                    <div className="grid gap-4 md:grid-cols-3">
                        {QUICK_FACTS.map((fact) => {
                            const Icon = fact.icon;
                            return (
                                <div
                                    key={fact.title}
                                    className="rounded-2xl border border-[var(--color-brand-border)] bg-[var(--color-brand-ai-bg)] p-5"
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[linear-gradient(135deg,rgba(34,211,238,0.12),rgba(16,185,129,0.10))] text-[var(--color-brand-primary)]">
                                            <Icon size={22} />
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-black uppercase tracking-[0.14em] text-[var(--color-brand-text)]">
                                                {fact.title}
                                            </h3>
                                            <p className="mt-2 text-sm leading-7 text-[var(--color-brand-text-secondary)]">
                                                {fact.text}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </motion.div>

                {/* Main layout */}
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
                    {/* Sidebar */}
                    <motion.aside
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, margin: '-80px' }}
                        className="lg:col-span-4 xl:col-span-3"
                    >
                        <div className="sticky top-24 rounded-[2rem] border border-[var(--color-brand-border)] bg-[linear-gradient(180deg,rgba(10,18,28,0.98),rgba(6,14,22,0.98))] p-6 shadow-[0_18px_50px_rgba(0,0,0,0.12)]">
                            <h3 className="mb-4 text-sm font-black uppercase tracking-[0.18em] text-[var(--color-brand-text-secondary)]">
                                On this page
                            </h3>

                            <nav className="space-y-2">
                                {SECTIONS.map((section) => {
                                    const Icon = section.icon;
                                    return (
                                        <a
                                            key={section.id}
                                            href={`#${section.id}`}
                                            className="flex items-center gap-3 rounded-xl border border-transparent px-3 py-3 text-sm font-medium text-[var(--color-brand-text-secondary)] transition-all hover:border-[var(--color-brand-primary)]/20 hover:bg-[var(--color-brand-primary)]/8 hover:text-[var(--color-brand-text)]"
                                        >
                                            <Icon size={16} className="text-[var(--color-brand-primary)]" />
                                            <span>{section.title}</span>
                                        </a>
                                    );
                                })}
                            </nav>
                        </div>
                    </motion.aside>

                    {/* Content */}
                    <div className="space-y-6 lg:col-span-8 xl:col-span-9">
                        {/* Fast action card */}
                        <motion.div
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, margin: '-60px' }}
                            className="rounded-[2rem] border border-emerald-400/18 bg-[linear-gradient(135deg,rgba(34,211,238,0.08),rgba(16,185,129,0.08),rgba(255,255,255,0.02))] p-6 shadow-[0_18px_50px_rgba(0,0,0,0.12)]"
                        >
                            <div className="flex flex-col items-start justify-between gap-5 md:flex-row md:items-center">
                                <div>
                                    <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.18em] text-emerald-300">
                                        <Sparkles size={13} />
                                        Fastest option
                                    </div>
                                    <h2 className="text-2xl font-black text-[var(--color-brand-text)]">
                                        Delete your account directly in the app
                                    </h2>
                                    <p className="mt-2 text-sm leading-7 text-[var(--color-brand-text-secondary)]">
                                        Open LesslyKilo, go to <span className="font-semibold text-[var(--color-brand-text)]">Settings</span>,
                                        then <span className="font-semibold text-[var(--color-brand-text)]">Account</span>, then
                                        <span className="font-semibold text-[var(--color-brand-text)]"> Delete Account</span>.
                                    </p>
                                </div>

                                <a
                                    href="#delete-in-app"
                                    className="inline-flex items-center gap-2 rounded-full border border-[var(--color-brand-primary)]/20 bg-[var(--color-brand-primary)]/10 px-5 py-3 text-sm font-semibold text-[var(--color-brand-text)] transition-all hover:border-[var(--color-brand-primary)]/30 hover:bg-[var(--color-brand-primary)]/14"
                                >
                                    See steps
                                    <ChevronRight size={16} className="text-[var(--color-brand-primary)]" />
                                </a>
                            </div>
                        </motion.div>

                        {SECTIONS.map((section, idx) => {
                            const Icon = section.icon;
                            return (
                                <motion.section
                                    key={section.id}
                                    id={section.id}
                                    variants={fadeUp}
                                    initial="hidden"
                                    whileInView="show"
                                    viewport={{ once: true, margin: '-60px' }}
                                    transition={{ delay: idx * 0.04 }}
                                    className="rounded-[2rem] border border-[var(--color-brand-border)] bg-[linear-gradient(180deg,rgba(10,18,28,0.98),rgba(6,14,22,0.98))] p-8 shadow-[0_18px_50px_rgba(0,0,0,0.12)]"
                                >
                                    <div className="mb-6 flex items-center gap-4">
                                        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,rgba(34,211,238,0.12),rgba(16,185,129,0.10))] text-[var(--color-brand-primary)] shadow-[0_10px_24px_rgba(34,211,238,0.10)]">
                                            <Icon size={26} />
                                        </div>

                                        <h2 className="text-2xl font-black text-[var(--color-brand-text)] md:text-3xl">
                                            {section.title}
                                        </h2>
                                    </div>

                                    <div className="space-y-5">
                                        {section.items.map((item) => (
                                            <div
                                                key={item.label}
                                                className="rounded-2xl border border-[var(--color-brand-border)] bg-[var(--color-brand-ai-bg)]/70 p-5"
                                            >
                                                <h3 className="text-sm font-black uppercase tracking-[0.16em] text-[var(--color-brand-primary)]">
                                                    {item.label}
                                                </h3>
                                                <p className="mt-3 text-sm leading-7 text-[var(--color-brand-text-secondary)] md:text-[15px]">
                                                    {item.text}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </motion.section>
                            );
                        })}

                        {/* Important notice */}
                        <motion.div
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true }}
                            className="rounded-[2rem] border border-amber-400/18 bg-amber-400/5 p-6 shadow-[0_18px_50px_rgba(0,0,0,0.10)]"
                        >
                            <div className="flex items-start gap-4">
                                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-amber-400/10 text-amber-300">
                                    <AlertTriangle size={22} />
                                </div>
                                <div>
                                    <h3 className="text-lg font-black text-[var(--color-brand-text)]">
                                        Important
                                    </h3>
                                    <p className="mt-2 text-sm leading-7 text-[var(--color-brand-text-secondary)]">
                                        If you have an active subscription through Google Play or Apple App Store,
                                        remember to cancel it separately through that platform to avoid future renewals.
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>

                {/* Footer strip */}
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true }}
                    className="mt-12 rounded-[2rem] border border-[var(--color-brand-primary)]/12 bg-[linear-gradient(135deg,rgba(34,211,238,0.08),rgba(16,185,129,0.06),rgba(255,255,255,0.02))] p-6 shadow-[0_18px_50px_rgba(0,0,0,0.16)] md:p-8"
                >
                    <div className="flex flex-col items-start justify-between gap-5 md:flex-row md:items-center">
                        <div>
                            <h3 className="text-xl font-black text-[var(--color-brand-text)] md:text-2xl">
                                Need help with deletion?
                            </h3>
                            <p className="mt-2 max-w-3xl text-sm leading-7 text-[var(--color-brand-text-secondary)] md:text-base">
                                If you cannot access your account or need assistance with deleting your data,
                                contact us and we will guide you through the process.
                            </p>
                        </div>

                        <a
                            href="mailto:caselabs91@gmail.com?subject=LesslyKilo%20Account%20Deletion%20Request"
                            className="inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-5 py-3 text-sm font-semibold text-[var(--color-brand-text)] transition-all hover:border-emerald-300/30 hover:bg-emerald-400/14"
                        >
                            <Mail size={16} className="text-emerald-300" />
                            Request deletion by email
                        </a>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}